import { Product } from "@omni/types/api/product/bff/product.model";
import { DeliveryType } from "@omni/types/api/product/bff/shared/enums/deliveryType.enum";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { RequireChildrenFor } from "@omni/types/components/utils/children.types";
import { isDefined } from "@omni/utils/helper.utils";
import { include } from "@omni/utils/object.utils";
import { createContext, use, useMemo } from "react";

import { useBFFProductServiceApi } from "@/api/hooks/productService.hook";
import { usePaintConfiguratorContext } from "@/components/PaintConfigurator/context/paintConfigurator.context";
import { getFeature } from "@/components/PaintConfigurator/helpers/feature.helper";
import {
    FeatureCodes,
    FeatureInfo,
    VolumeInfo
} from "@/components/PaintConfigurator/paintConfigurator.types";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { getAllowedDeliveryTypes } from "@/utils/deliveryMethod";

import { ProductFilters } from "./products.filters";

type ProductsContextValue = {
    products: Array<Product<ProductFieldSet.Full>>;
    isLoading: boolean;

    selectedFinish: FeatureInfo | null;
    setSelectedFinish: (finish: string) => void;

    selectedVolume: VolumeInfo | null;
    setSelectedVolume: (volume: string) => void;

    selectedDeliveryType: DeliveryType | null;
    setSelectedDeliveryType: (deliveryMethod: DeliveryType) => void;

    finishes: Map<string, FeatureInfo>;
    volumes: Map<string, VolumeInfo>;
};

type ProductsContextProviderProps = RequireChildrenFor<{
    colorId?: string;
    urlState?: string;
}>;

export const ProductsContext = createContext<ProductsContextValue | undefined>(
    undefined
);
ProductsContext.displayName = "ProductsContext";

export const ProductsContextProvider = ({
    colorId,
    urlState,
    children
}: ProductsContextProviderProps) => {
    const { t } = useGranite();
    const { setState, state } = usePaintConfiguratorContext();

    const [{ data: products = [], loading: loadingProducts }] =
        useBFFProductServiceApi<
            any,
            Array<Product<ProductFieldSet.Full>>,
            Array<Product<ProductFieldSet.Full>>
        >(
            {
                url: `/paint-colors/paints/:colorId/products`,
                method: "GET",
                params: {
                    fields: ProductFieldSet.Full
                },
                ...include(colorId, (value) => ({
                    urlParams: {
                        colorId: value
                    }
                }))
            },
            { autoCancel: false, manual: false, useCache: true }
        );

    const onSelectFinish = (finish: string) => {
        if (state.finish === finish) {
            setState({ finish: undefined });
            return;
        }
        setState({ finish });
    };

    const onSelectVolume = (volume: string) => {
        if (state.volume === volume) {
            setState({ volume: undefined });
            return;
        }
        setState({ volume });
    };

    const onSelecteDeliveryType = (deliveryType: DeliveryType) => {
        if (state.deliveryType === deliveryType) {
            setState({ deliveryType: undefined });
            return;
        }
        setState({ deliveryType });
    };

    const enhancedProducts = useMemo(
        () =>
            ProductFilters.filter(state.collection)(products)
                .filter(
                    (b) =>
                        (!state.finish ||
                            getFeature(t, b, FeatureCodes.FinishFeatureCode)
                                ?.value === state.finish) &&
                        (!state.volume ||
                            getFeature(t, b, FeatureCodes.VolumeFeatureCode)
                                ?.value === state.volume) &&
                        (!state.deliveryType ||
                            getAllowedDeliveryTypes(
                                b.productVisibilityType
                            ).includes(state.deliveryType))
                )
                .map((product) => ({
                    ...product,
                    url: `${product.url}${urlState ? `?pc=${urlState}` : ""}`
                })),
        [
            products,
            state.finish,
            state.volume,
            state.deliveryType,
            urlState,
            t,
            state.collection
        ]
    );

    const finishes = enhancedProducts.reduce((acc, product) => {
        const finish = getFeature(t, product, FeatureCodes.FinishFeatureCode);
        if (isDefined(finish)) {
            acc.set(finish.value, finish);
        }
        return acc;
    }, new Map<string, FeatureInfo>());

    const volumes = enhancedProducts
        .map((product) => {
            const volume = getFeature(
                t,
                product,
                FeatureCodes.VolumeFeatureCode
            );

            const coverage = getFeature(
                t,
                product,
                FeatureCodes.CoverageFeatureCode,
                t("paintConfigurator.coverage.label")
            );

            if (!isDefined(volume) || !isDefined(coverage)) return null;

            const numericalValue = Number(
                volume.value.replace(/[^\d,.-]/g, "").replaceAll(",", ".")
            );

            return {
                value: Number.isNaN(numericalValue) ? 0 : numericalValue,
                volume: volume,
                coverage: coverage
            };
        })
        .filter(isDefined)
        .toSorted((a, b) => a.value - b.value)
        .reduce((acc, { volume, coverage }) => {
            acc.set(volume.value, {
                volume,
                coverage
            });

            return acc;
        }, new Map<string, VolumeInfo>());

    return (
        <ProductsContext
            value={{
                products: enhancedProducts,
                isLoading: loadingProducts,

                selectedDeliveryType: state.deliveryType || null,
                selectedFinish: finishes.get(state.finish ?? "") || null,
                selectedVolume: volumes.get(state.volume ?? "") || null,

                setSelectedFinish: onSelectFinish,
                setSelectedVolume: onSelectVolume,
                setSelectedDeliveryType: onSelecteDeliveryType,

                finishes,
                volumes
            }}
        >
            {children}
        </ProductsContext>
    );
};

export const useProductsContext = () => {
    const context = use<ProductsContextValue | undefined>(ProductsContext);

    if (!isDefined(context)) {
        throw new Error(`${ProductsContext.displayName} is undefined.`);
    }

    return context;
};
